export const SALON = {
  name: "Honeycomb Salon",
  tagline: "We bring out the most authentic beauty in you.",
  established: 2007,
  phone: "415-401-0100",
  phoneHref: "tel:+14154010100",
  text: "833-682-7469",
  textHref: "sms:+18336827469",
  email: "hello@honeycombsalon.com",
  address: {
    street: "3915 24th Street, Suite C",
    city: "San Francisco, CA 94114",
    neighborhood: "Noe Valley",
  },
  hours: [
    { day: "Monday", time: "Closed" },
    { day: "Tuesday", time: "10am – 6pm" },
    { day: "Wednesday", time: "10am – 6pm" },
    { day: "Thursday", time: "10am – 6pm" },
    { day: "Friday", time: "10am – 6pm" },
    { day: "Saturday", time: "10am – 6pm" },
    { day: "Sunday", time: "10am – 6pm" },
  ],
  hoursNote:
    "Extended hours (9am – 7pm) are available based on online scheduling. Walk-ins welcome when scheduling permits. Holiday hours may vary.",
  social: {
    instagram: "https://instagram.com/",
    pinterest: "https://pinterest.com/",
    yelp: "https://www.yelp.com/biz/honeycomb-salon-san-francisco",
  },
  certifications: ["Green Circle Salons", "Certified B Corp", "Keune Haircare", "Oway Organic"],
};

export type Expert = {
  name: string;
  role: string;
  pronouns: string;
  specialties: string[];
  quote?: string;
  initials: string;
};

export const EXPERTS: Expert[] = [
  {
    name: "Gillian Hanson",
    role: "Founder & Creative Director",
    pronouns: "She/Her",
    initials: "GH",
    specialties: [
      "Advanced Color Transformations",
      "Inspired Cuts",
      "Makeovers",
      "Makeup & Hair for Special Events",
      "Education & Public Speaking",
    ],
    quote: "Gillian is a genius stylist but also really listens, which is key.",
  },
  {
    name: "Maddie Giglio",
    role: "Junior Hairstylist",
    pronouns: "She/Her",
    initials: "MG",
    specialties: ["Dimensional Color", "Hair Extensions (K-tips & Tape-ins)"],
  },
  {
    name: "Michael Generalao",
    role: "Hairstylist",
    pronouns: "He/Him",
    initials: "MG",
    specialties: ["Haircut Specialist", "Vidal Sassoon-Inspired Looks", "Dimensional Blondes"],
    quote: "Michael is one of the nicest people and does fantastic work.",
  },
  {
    name: "Alexandria Rose",
    role: "Hairstylist",
    pronouns: "They/Them",
    initials: "AR",
    specialties: [
      "Precision Cuts",
      "Vidal Sassoon-style Bobs",
      "Dimensional Blondes",
      "Invisible Bead Extensions",
    ],
  },
  {
    name: "James Strife",
    role: "Independent Hairstylist",
    pronouns: "He/Him",
    initials: "JS",
    specialties: [
      "Lived-in Color",
      "Balayage",
      "Grey Coverage",
      "Keratin Treatments",
      "Hair Recovery Expert",
    ],
  },
  {
    name: "Meg Brittain",
    role: "Independent Hairstylist",
    pronouns: "She/Her",
    initials: "MB",
    specialties: [
      "All Hair Types",
      "Precision Cuts",
      "Shags & Bobs",
      "Dimensional Color",
      "Color Correction",
    ],
  },
  {
    name: "Gino Anthony",
    role: "Independent Hairstylist",
    pronouns: "He/Him",
    initials: "GA",
    specialties: ["Master Colorist", "Balayage Specialist"],
  },
];

export const SUPPORT_TEAM = [
  { name: "Danny Mariano", role: "Salon Manager", pronouns: "He/Him" },
  { name: "Harley", role: "Hospitality Hostess, Retired", pronouns: "She/Her" },
  { name: "Madigan", role: "Hospitality Host, Retired", pronouns: "He/Him" },
];

export type ServiceCategory = {
  name: string;
  blurb: string;
  items: { name: string; desc: string; price: string }[];
};

export const SERVICES: ServiceCategory[] = [
  {
    name: "Haircuts & Styling",
    blurb:
      "A customized cut tailored to your face shape, texture, personality and lifestyle — finished with a style you can recreate at home.",
    items: [
      { name: "Signature Haircut", desc: "Consultation, shampoo, precision cut and style.", price: "from $85" },
      { name: "Express Trim", desc: "Maintenance cut to keep your shape between visits.", price: "from $55" },
      { name: "Blowout & Style", desc: "Wash and professional blow-dry styling.", price: "from $60" },
      { name: "Special Event Hair", desc: "Updos and styling for weddings and occasions.", price: "from $120" },
    ],
  },
  {
    name: "Color",
    blurb:
      "From natural, lived-in balayage to bold fantasy colors — color designed around your goals and the health of your hair.",
    items: [
      { name: "Balayage", desc: "Hand-painted, dimensional, sun-kissed color.", price: "from $185" },
      { name: "Full Highlights", desc: "Brightening and dimension throughout.", price: "from $165" },
      { name: "Single Process Color", desc: "All-over color or grey coverage.", price: "from $95" },
      { name: "Color Correction", desc: "Custom, consultation-based transformation.", price: "by quote" },
      { name: "Fantasy Color", desc: "Vivid, expressive, fashion-forward shades.", price: "by quote" },
    ],
  },
  {
    name: "Extensions & Treatments",
    blurb:
      "Add length, volume and shine, or restore strength with reparative treatments that bring damaged hair back to life.",
    items: [
      { name: "Hair Extensions", desc: "K-tips, tape-ins and invisible bead methods.", price: "by quote" },
      { name: "Keratin Smoothing", desc: "Frizz-reducing, smoothing treatment.", price: "from $250" },
      { name: "Bond Repair Treatment", desc: "In-salon strengthening for compromised hair.", price: "from $45" },
      { name: "Scalp & Hair Recovery", desc: "Nourishing ritual for scalp and strands.", price: "from $50" },
    ],
  },
  {
    name: "Makeup & Special Events",
    blurb:
      "Professional makeup application for photoshoots, weddings and milestone moments — natural to glam.",
    items: [
      { name: "Event Makeup", desc: "Polished application for any occasion.", price: "from $110" },
      { name: "Bridal Trial + Day-of", desc: "Trial session plus wedding-day application.", price: "by quote" },
    ],
  },
];

export const ETHOS = {
  heading: "Our Ethos",
  subheading: "We bring out the most authentic beauty in you.",
  intro:
    "We are artists, empaths, cultural connoisseurs, social connectors, friends and healers. We believe hair is an artistic medium for personal expression — without limitations based on age, race, or gender identity.",
  promises: [
    { title: "We hear you", text: "We not only listen, we hear you." },
    { title: "We see you", text: "We see your vision of your best authentic self." },
    { title: "We create with you", text: "We bring your vision to reality." },
  ],
  values: [
    {
      title: "Artistry & Design",
      text: "A passionate, artistic approach to hair and style, customized to each person.",
    },
    {
      title: "Inclusivity",
      text: "Hair design transcends age, race, or gender identity. Everyone is welcome here.",
    },
    {
      title: "Environmental Responsibility",
      text: "Operating as a green business, certified by Green Circle Salons, is the only way to be.",
    },
    {
      title: "Wellness",
      text: "We champion self-care, love, reflection and life-balance for our guests and our team.",
    },
  ],
};

export const TESTIMONIALS = [
  {
    quote: "Gillian is a genius stylist but also really listens, which is key. I always leave feeling like the best version of myself.",
    author: "Noe Valley regular",
  },
  {
    quote: "Michael is one of the nicest people and does fantastic work. The whole salon feels warm and welcoming.",
    author: "Honeycomb guest",
  },
  {
    quote: "Best balayage in San Francisco. They truly see your vision and bring it to life — and it's a sustainable salon too.",
    author: "Five-star Yelp review",
  },
];
